import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import type { parseUserNode, UserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import { ProfileIcon } from '/@/views/dashboard/squad/components/profile-icon';

export type GrowerTeamRow = ReturnType<typeof parseUserNode> & { distributorOrSquad: string; role: string };

const profileIconType = (nodeType: UserNode['type']): 'rv' | 'consultant' | 'advisor' | 'primary' =>
    nodeType === 'consultant' || nodeType === 'advisor' || nodeType === 'rv' ? nodeType : 'primary';

const TeamAccordion = ({ teamData }: { teamData: GrowerTeamRow[] }) => {
    return (
        <Accordion
            disableGutters
            elevation={0}
            sx={{
                backgroundColor: 'transparent',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                aria-controls='panel1-content'
                id='panel1-header'
                sx={{
                    p: 0,
                    minHeight: 'auto',
                    justifyContent: 'flex-start',
                    '& .MuiAccordionSummary-content': {
                        m: 0,
                        flexGrow: 0,
                    },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        ml: 0.5,
                    },
                }}
                expandIcon={<ExpandMoreIcon color='primary' />}
            >
                <Typography color='primary' variant='body1' fontWeight='bold'>
                    {_('Ver equipo de trabajo')}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0, pt: 2 }}>
                <Stack className='mp-mask' spacing={2}>
                    <Typography color='primary' variant='body1' textAlign='left'>
                        {_(
                            'Visualizá quiénes tienen acceso a la información de este productor. Si detectás que alguna persona ya no debería estar vinculada, comunicalo con tu superior para actualizar el equipo.'
                        )}
                    </Typography>
                    {teamData.length > 0 && (
                        <Table size='small'>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        '& th': { border: 0 },
                                        backgroundColor: '#f5f5f5',
                                    }}
                                >
                                    <TableCell>
                                        <Typography variant='body2' color='primary' fontWeight='bold'>
                                            {_('Rol')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant='body2' color='primary' fontWeight='bold'>
                                            {_('Distribuidor/Squad')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant='body2' color='primary' fontWeight='bold'>
                                            {_('Contacto')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {teamData.map((row, index) => {
                                    return (
                                        <TableRow
                                            key={`${row.type}-${row.id}`}
                                            sx={{
                                                '&:last-child td': { border: 0 },
                                                ...(index === 0 && { '& td': { pt: 2 } }),
                                            }}
                                        >
                                            <TableCell>
                                                <Stack direction='row' spacing={1.5} alignItems='center'>
                                                    <ProfileIcon
                                                        key={row.id}
                                                        userNode={row}
                                                        type={profileIconType(row.type)}
                                                        size={26}
                                                        sx={{
                                                            position: 'relative',
                                                            zIndex: teamData.length - index,
                                                        }}
                                                    />
                                                    <Stack>
                                                        <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            fontWeight='bold'
                                                        >
                                                            {row.role}
                                                        </Typography>
                                                        <Typography variant='body2' color='primary' fontWeight='bold'>
                                                            {row.name}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant='body2' color='primary'>
                                                    {row.distributorOrSquad || '-'}
                                                </Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Stack spacing={0.5} direction='column'>
                                                    <Stack direction='row' spacing={0.5} alignItems='center'>
                                                        <EmailIcon fontSize='small' sx={{ color: 'grey' }} />
                                                        <Typography variant='body2' color='primary'>
                                                            {row.email || '-'}
                                                        </Typography>
                                                    </Stack>
                                                    {row.phone && (
                                                        <Stack direction='row' spacing={0.5} alignItems='center'>
                                                            <LocalPhoneIcon fontSize='small' sx={{ color: 'grey' }} />
                                                            <Typography variant='body2' color='primary'>
                                                                {row.phone}
                                                            </Typography>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};

export default TeamAccordion;
